import { isHotLot } from '@auctisync/shared';
import { Badge } from '@auctisync/ui';

interface LotBadgesProps {
  /** Bid count on the lot. */
  bidCount: number | null | undefined;
  /** Whether reserve is met (or no reserve set). */
  reserveMet?: boolean | null;
  /** Multi-unit lot quantity. */
  quantity?: number | null;
  /** When the lot was created (ISO). */
  createdAt?: string | null;
  /** Hide an individual badge if its info is shown more prominently elsewhere
   *  on the page (e.g., lot detail page already shows the reserve-met chip in
   *  the header). */
  exclude?: Array<'hot' | 'new' | 'reserve_met' | 'multi_unit'>;
}

// 🔴 "Hot" IS DEFINED ONCE, in `@auctisync/shared`. This file used to carry its
// own threshold of >=10 while the sale-results page used >20 (Jim, 2026-09-08),
// so the SAME lot could be Hot on one page and not on another — a bidder reading
// two of our screens would have been told two different things about one lot.
// See `memory/feedback_no_mechanical_divergence.md`.
const NEW_LOT_AGE_MS = 24 * 60 * 60 * 1000; // <24h since creation is "New"

/**
 * Module #23 chunk 6 — derived lot curation badges.
 *
 * Pure UI; reads existing lot fields, no schema change. Renders 0+ badges
 * inline. Order: Hot, New, Reserve Met, Multi-Unit.
 */
export function LotBadges({
  bidCount,
  reserveMet,
  quantity,
  createdAt,
  exclude = [],
}: LotBadgesProps) {
  const badges: { key: string; element: React.ReactElement }[] = [];

  // `false` for the flag: this component is used on surfaces that already show
  // the bid count, so there is no hidden count for the badge to leak.
  if (!exclude.includes('hot') && isHotLot(bidCount ?? 0, false)) {
    badges.push({
      key: 'hot',
      element: (
        <Badge variant="warning" title={`${bidCount} bids — hot lot`}>
          🔥 Hot
        </Badge>
      ),
    });
  }

  if (!exclude.includes('new') && createdAt) {
    const ageMs = Date.now() - new Date(createdAt).getTime();
    if (ageMs >= 0 && ageMs < NEW_LOT_AGE_MS) {
      badges.push({
        key: 'new',
        element: <Badge variant="live">✨ New</Badge>,
      });
    }
  }

  if (!exclude.includes('reserve_met') && reserveMet === true) {
    badges.push({
      key: 'reserve_met',
      element: <Badge variant="success">Reserve met</Badge>,
    });
  }

  if (!exclude.includes('multi_unit') && (quantity ?? 0) > 1) {
    badges.push({
      key: 'multi_unit',
      element: <Badge variant="outline">×{quantity} units</Badge>,
    });
  }

  if (badges.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
      {badges.map((b) => (
        <span key={b.key}>{b.element}</span>
      ))}
    </div>
  );
}
